/**
 * Query keys, in one place, because one of them is load-bearing.
 *
 * ⚠️ **The dashboard payload is viewer-dependent** (ADR 0015, ADR 0006): two users legitimately
 * receive different bodies from the same URL — an Interviewer's omits `health`, `pipeline` and
 * `interviewer_activity`, and Section 4's histogram is blind-filtered per caller, so a user who
 * owes feedback for a candidate does not see that candidate's scores. A cache keyed on the URL
 * alone would serve one viewer's blinded aggregate to another, **and every backend test would stay
 * green while it happened**. Every key for a viewer-dependent resource therefore carries the
 * viewer's id, and `api.test.ts` asserts it.
 */
export const queryKeys = {
  session: () => ['session'] as const,
  branding: () => ['branding'] as const,
  dashboard: (viewerId: number, period?: string) =>
    ['dashboard', viewerId, period ?? null] as const,
  /**
   * §6.2 Section 1's list, unabridged — `GET /api/v1/dashboard/outstanding/` (TASK-157).
   *
   * ⚠️ **Inside the `dashboard` family rather than a family of its own, and that is the point of the
   * `'outstanding'` segment** — the shape `queryKeys.interview` and `queryKeys.questionSet` both take.
   * Submitting an interview takes a row off this list and off Section 1's five in the same instant, and
   * `queryKeyRoots.dashboard` is what TASK-084 invalidates after a submission; keyed as
   * `['outstanding', …]` this list would sit outside that root and go on showing work that is no longer
   * owed for `staleTime` after the very act that discharged it.
   *
   * **The viewer is not optional**, for the reason the dashboard key states one line up: the route is
   * **narrowed** per caller — an `Interviewer` is answered their own outstanding drafts, a
   * `Recruiter`/`Admin` the tenant's, decided in the queryset — so two callers legitimately receive
   * different rows from the same URL. That is `roleOptions`' case, not `roles`': *narrowed* takes a
   * viewer segment, *refused* does not.
   *
   * The query is in it for `queryKeys.roles`' reason — `?page=` changes the body the one URL answers
   * with, and a constant key would write page 2 into the entry page 1 is read from.
   */
  outstanding: (viewerId: number, query?: Record<string, unknown>) =>
    ['dashboard', 'outstanding', viewerId, query ?? null] as const,
  people: (viewerId: number, filters?: Record<string, unknown>) =>
    ['people', viewerId, filters ?? null] as const,
  considerations: (viewerId: number, filters?: Record<string, unknown>) =>
    ['considerations', viewerId, filters ?? null] as const,
  /**
   * The person card, and the viewer is **not** optional here (ADR 0006, backend TASK-033):
   * `average_score`, `latest_recommendation` and every interview row's figures are computed over the
   * caller's own blind-filtered set, so a colleague who owes no feedback receives numbers where the
   * caller holding an unsubmitted interview receives `null` — from the same URL, for an Admin and a
   * Recruiter too, because the gate keys on owing feedback and not on group. Two viewers must
   * therefore hold two entries for `/api/v1/people/{id}/`.
   */
  person: (viewerId: number, personId: number) => ['person', viewerId, personId] as const,
  /**
   * §6.4's duplicate pre-check, keyed on the three match keys as they were typed — so the answer for
   * *"Ada Lovelace"* is not served for *"Ada Lovelice"*, and re-typing a name the recruiter already
   * paused on costs no second request on a route throttled at 300/min per user (BUG-011).
   *
   * The viewer is in it for the same reason it is in every other key here: `POST
   * /people/check-duplicates/` is tenant-scoped and is not narrowed per caller today, but that is a
   * property of the endpoint rather than of the client, and the cost of carrying the viewer is one
   * cache entry. This key holds a name, an email and a phone number **in memory**, which is where the
   * whole point of the route being a POST lies (`.claude/rules/security.md`): none of it reaches a
   * URL, a log or a history entry.
   *
   * ⚠️ **What bounds its life, corrected at the sprint-end review** (`code-reviewer` and
   * `security-auditor` both, 2026-07-28). This said *"`queryClient.clear()` on sign-in is what bounds
   * its life"*, and that was the whole of it — so an erasure (ADR 0009) left the erased person's
   * `full_name`, `email`, `phone` and `source` sitting under this key, keyed on the very name that
   * would fetch them back, and §6.4 re-offered them with **no request at all**. Three things bound it
   * now, and each is needed: `gcTime: 0` on the pre-check (`AddCandidateDialog`), so a body full of
   * other people's contacts does not outlive the dialog that asked for it; `removeQueries` on
   * `queryKeyRoots.duplicateCheck` after an erasure and after a rename; and the sign-in clear.
   */
  duplicateCheck: (viewerId: number, keys: Record<string, unknown>) =>
    ['duplicate-check', viewerId, keys] as const,
  /**
   * The interview list, `GET /api/v1/interviews/` — **the viewer and the query, and until TASK-079
   * only the viewer.**
   *
   * ⚠️ **The query segment is the half that was missing, and the failure it allows is a candidate's
   * conversations rendered under another candidate's id.** This builder was `(viewerId) =>
   * ['interviews', viewerId]`, so `?person_id=1` and `?person_id=2` were **one entry**: §6.7's *Other
   * interviews* block would show whichever person was asked for last, with every existing test green.
   * That is Sprint 10's `queryKeys.roles` finding one screen earlier (see its docblock), and it is
   * fixed here **before** a screen can ask a filtered question rather than after one has. The three
   * narrowing parameters the route declares — `?consideration_id=`, `?person_id=`, `?submitted=` — plus
   * the page all change the body the one URL answers with.
   *
   * ⚠️ **The viewer stays, and on this route it is not a precaution.** ADR 0006 decides the *shape* of
   * every row per caller: an interview one viewer reads in full is metadata alone to another, from
   * the same URL, for an `Admin` as much as for an `Interviewer` (`InterviewOut`'s own docstring: *"the
   * same interview id legitimately serialises differently for two callers … nothing may cache this
   * payload on the URL alone"*). This is therefore the opposite case from `roles`, where the route is
   * *refused* per caller and a viewer segment would buy nothing.
   */
  interviews: (viewerId: number, query?: Record<string, unknown>) =>
    ['interviews', viewerId, query ?? null] as const,
  /**
   * One interview, `GET /api/v1/interviews/{interview_id}/` — §6.6 and §6.7's own read.
   *
   * ⚠️ **Inside the `interviews` family rather than a family of its own, and that is the point of
   * the `'detail'` segment** — the shape `queryKeys.questionSet` takes inside `question-sets`, for
   * a stronger reason than a stale count. **Submitting one interview changes the shape of others**:
   * the moment this viewer owes no unsubmitted feedback for a person, that person's other submitted
   * interviews stop being the blinded shape and come back in full (ADR 0006, and the submit
   * endpoint says so — *"it also opens **your own** blind gate"*). So a submission invalidates not
   * only the list but every detail entry this viewer holds, and `queryKeyRoots.interviews` has to
   * reach all of them; keyed as `['interview', …]` it would sit outside that root and a blinded
   * payload would go on rendering for `staleTime` after the very act that opened it. `api.test.ts`
   * asserts the root really is this builder's prefix too.
   *
   * The viewer is in it for the reason above: two viewers legitimately receive different bodies for one
   * interview id, so they must hold two entries.
   */
  interview: (viewerId: number, interviewId: number) =>
    ['interviews', 'detail', viewerId, interviewId] as const,
  /**
   * The vacancy catalogue — **one of the two keys with no viewer**, and the only one that also has to
   * carry its query.
   *
   * ⚠️ **A query segment is not a viewer segment.** Everything below is about the *viewer*, and it
   * stands unchanged; the list query is a separate matter and is the reason this builder takes an
   * argument at all (TASK-070). `GET /api/v1/roles/` is paginated, and until Sprint 10 this key was
   * the constant `['roles']` — so the first screen to ask for `?page=2` would have written page 2's
   * vacancies into the one entry §6.3's `VacancyFilter` and §6.4's `AddCandidateDialog` read, and
   * both of them would have rendered page 2 as *the* catalogue with every existing test green. That
   * is the same shape as Sprint 8's `placeholderData` and Sprint 9's ungated catalogue read: a cache
   * serving one request's body to a caller that asked a different question. Two different queries are
   * two entries, and `vacancies.test.tsx` mounts the filter beside a grid holding page 2 to say so.
   *
   * ⚠️ **The reason recorded here was false, and both reviewers said so independently at Sprint 9's
   * sprint-end pass (2026-07-28).** It read: *"exempt on a condition — `GET /api/v1/roles/` is
   * tenant-scoped and group-gated and is never narrowed per caller, so every caller in a tenant
   * receives the same list. The moment the backend narrows it per user … the viewer segment goes in
   * here."* An **Interviewer's answer on that route is 403** — not a narrower list, no list at all —
   * so the note's own condition had already been met and the exemption stood on a sentence that was
   * not true.
   *
   * That 403 is the route's own group gate — `read_access = require_group(GROUP_ADMIN,
   * GROUP_RECRUITER)` in `backend/src/apps/vacancies/api.py`, shipped with Sprint 2 (`b1e125d`), and
   * pinned by `Route("get", ROLES, None, (401, 403, 200, 200, 403, 403))` in
   * `backend/tests/test_permission_matrix.py`. **It is not TASK-029**, which is Sprint 4's rule that
   * the *candidate* surface refuses an Interviewer with 404 rather than 403; the first draft of this
   * corrected note mis-cited it, which the re-review caught — a paragraph whose whole subject is a
   * reason that turned out false is the last place to put a citation nobody checked.
   *
   * What is true: the route is **refused** per caller rather than narrowed, so every caller who is
   * answered at all is answered the same body, and no viewer's list can be served to another. The
   * hazard is the other one — **a cached entry outliving the permission that filled it.** A viewer
   * segment would not close it, because the path is a demotion *without a re-login* (`useSession` has
   * `staleTime: 60_000`) and a demoted user keeps their id. What closes it is that the reader gates
   * on the group as well as the request: `VacancyFilter` passes the catalogue to `vacancyOptions`
   * only when `mayReadVacancyCatalogue`, and `candidateControls.test.tsx` asserts the **rendered
   * options** and not merely the requests.
   *
   * If the backend ever answers this route with a genuinely per-caller **list**, the viewer goes in
   * here — that is a different condition from the one written above, and it has not been met.
   */
  /**
   * The staff directory, `GET /api/v1/users/` — **one of the two keys with no viewer**, and it carries
   * its query for `roles`' reason rather than a weaker copy of it.
   *
   * ⚠️ **The query segment is not optional here, and its reason is `page`** (corrected 2026-08-05,
   * BUG-048). A constant key would write a *narrowed* answer into the entry the whole directory is read
   * from — the next reader would get one page of accounts as **the** directory, with every test green.
   * That is Sprint 10's `roles` finding and Sprint 8's `placeholderData` finding in one shape, and it is
   * fixed here before a screen can produce it. ⚠️ This paragraph argued it from **`?search=`** — *"§6.5's
   * picker asks with `?search=` as somebody types"* — which no screen ever did: the parameter had no
   * caller in either repository and was removed. The rule stands on `?page=` and `?page_size=`, which are
   * in the document, and `api.test.ts` pins it so a future reader cannot retire the segment with the
   * parameter that was only ever the example.
   *
   * **Viewer-less, on the same argument `roles` makes:** this route is *refused* per caller (an
   * `Interviewer` is 403, ADR 0016) and never *narrowed*, so no caller's list can be served to
   * another; what a viewer segment would not close is an entry outliving the permission that filled
   * it, which happens through a demotion without a re-login and therefore with an unchanged user id.
   * That is closed at the **read** — the caller gates on `mayReadStaffDirectory` as well as on
   * `enabled` — which is the pattern `VacancyFilter` established.
   */
  staff: (query?: Record<string, unknown>) => ['staff', query ?? null] as const,
  roles: (query?: Record<string, unknown>) => ['roles', query ?? null] as const,
  /**
   * §6.3's vacancy-filter vocabulary (`GET /api/v1/roles/options/`, backend TASK-108) — **keyed on the
   * viewer**, unlike `roles` above.
   *
   * ⚠️ The distinction is the route's, not a preference: the catalogue answers every caller who may call
   * it the *same* set (which is why its key carries no viewer), while this one is **narrowed per
   * caller** — an `Interviewer` is answered only the vacancies their own assignments touch. One shared
   * entry would serve an `Interviewer`'s three vacancies to an `Admin`, or the tenant's twenty to an
   * `Interviewer`, on a viewer change under a mounted screen. It lives in the `roles` family so a
   * vacancy write invalidates it.
   */
  roleOptions: (viewerId: number) => ['roles', 'options', viewerId] as const,
  /*
   * ⚠️ **`queryKeys.role` stood here and is deleted** (2026-09-28, BUG-079). A block comment rather
   * than a docblock on purpose: it documents an absence, and a docblock here would be read as the
   * next key's own description. The key was `['roles', 'detail', roleId]`, one vacancy's, and its
   * **only** reader was §6.9's screen back when that screen named itself after a vacancy; ADR 0018
   * took the vacancy out of its address and TASK-135 deleted `roleQuery` with it, leaving a builder
   * no query used. It was kept then for a stated reason — the question of whether a key with no
   * reader earns its place was §6.8's and not that sprint's — and the answer, deferred to `BUGS.md`
   * and taken here, is no.
   *
   * **A cache key is the worst place in the client to hold something unread**, which is what makes
   * this a deletion rather than a tidy-up: `applyTenantAccent()` is the project's precedent for an
   * export with no caller (Sprint 8, refused twice by `code-reviewer`), and the failure mode here is
   * sharper — the next screen to want one vacancy reaches for the key that is already there, gets a
   * builder nobody ever invalidated on purpose, and inherits a stale read. A key arrives with its
   * query or not at all.
   *
   * **Nothing is lost by it.** The write path never named it: §6.8's dialogs invalidate
   * `queryKeyRoots.roles`, which is the **family's first segment** and therefore reaches every entry
   * under it — a single-vacancy entry included, on the day one exists again. `api.test.ts` asserts
   * this name stays gone, as it does for `questions` two families down.
   */
  /**
   * The tenant's question **sets**, `GET /api/v1/question-sets/` — §6.9's one list.

   * ⚠️ **`queryKeys.questions` stood beside this one until 2026-09-24 and is deleted with its route**
   * (ADR 0019). It was the tenant-wide library's key, viewer-less, on the argument that the route was
   * neither refused nor narrowed per caller — a wording **retracted** with the library itself. There is no such route and no such list: a question is
   * reachable only *through* its set, so the set's reach is the question's reach and **this** key is
   * the only one either noun has. The asymmetry that paragraph existed to explain is gone with it.
   *
   * **The viewer is in it, and on this route that is not a precaution.** The rows are **narrowed** per
   * caller — ADR 0018's 2026-08-12 reach entry, which ADR 0019 restates rather than re-decides —
   * because the migration named every set after its vacancy, so a flat tenant-wide list would hand an
   * `Interviewer` the catalogue `GET /api/v1/roles/` answers them 403. Two readers of this URL
   * therefore legitimately receive different rows, and one shared entry is one caller's list served to
   * another. That is exactly why `roleOptions` carries a viewer and `roles` does not: *narrowed* takes
   * a viewer segment, *refused* does not.
   *
   * The query is in it for `queryKeys.roles`' reason — `?include_retired=`, `?author_id=` and the page
   * all change the body the one URL answers with, and `?author_id=` is what §6.5's pre-fill asks with.
   */
  questionSets: (viewerId: number, query?: Record<string, unknown>) =>
    ['question-sets', viewerId, query ?? null] as const,
  /**
   * One set with its composition, `GET /api/v1/question-sets/{id}/`.
   *
   * Inside the `question-sets` family rather than a family of its own, and that is the point of the
   * `'detail'` segment — the shape `queryKeys.interview` takes inside `interviews`: composing a set
   * moves `active_question_count`, which is a column of every **row** of the list, so one root has to
   * reach the list and this entry alike.
   *
   * **No query segment, and that is a property of the read rather than an omission**: there is exactly
   * one way to ask for a set, `include_archived=true` (`questionSetQuery` says why), so two entries
   * for one set id could only ever hold the same body. *Show archived* filters what is held.
   */
  questionSet: (viewerId: number, setId: number) =>
    ['question-sets', 'detail', viewerId, setId] as const,
  /**
   * The sets one vacancy **offers**, `GET /api/v1/roles/{role_id}/question-sets/` — §6.8's control.
   *
   * In the `roles` family, so attaching or detaching invalidates it together with the grid whose
   * `active_question_count` the same write moves. **No viewer**, on `queryKeys.roles`' own argument:
   * this route is *refused* per caller (an `Interviewer` is 403, as on the catalogue itself) and never
   * narrowed, so no caller's list can be served to another.
   */
  roleQuestionSets: (roleId: number) => ['roles', 'question-sets', roleId] as const,
} as const;

/**
 * The **prefixes**, for the one thing a key builder cannot express: what a write invalidates.
 *
 * `queryKeys.considerations(7, filters)` names one entry, and a candidate created in §6.4 belongs to
 * every viewer's every filter — so the invalidation after a write matches on the family's first
 * segment, which React Query treats as a prefix. Writing `['considerations']` at each call site would
 * put the family name in three screens; `api.test.ts` asserts each root really is the prefix of the
 * builder beside it, so the two cannot drift apart.
 */
export const queryKeyRoots = {
  considerations: ['considerations'],
  person: ['person'],
  /**
   * The two families a **submission** moves, and the reason both are roots rather than keys (TASK-079).
   *
   * Submitting an interview is the one write in the product that changes four things at once: the person
   * card's average and latest recommendation (`person`), §6.3's row for that consideration
   * (`considerations`), the interview itself and every other interview this viewer holds for that person
   * — because ADR 0006's gate opens for them in the same instant — and §6.2's outstanding-feedback
   * count, which is the leak the dashboard exists to show. Two of the four already had a root; these are
   * the other two, and TASK-084 invalidates all four by root rather than naming a viewer, a filter or a
   * period at the call site.
   *
   * `interviews` is the family's first segment, so it reaches the list under every viewer and every
   * query **and** `queryKeys.interview`, which lives in this family for exactly that reason (see its
   * docblock). `dashboard` reaches every period.
   */
  interviews: ['interviews'],
  dashboard: ['dashboard'],
  /**
   * ⚠️ **The family a write must sometimes *remove* rather than refresh.** §6.4's pre-check holds a
   * whole other person — `PossibleDuplicateOut` carries `full_name`, `email`, `phone` and `source` —
   * under a key that is itself the name somebody typed. An erasure (ADR 0009) and a rename both make
   * every entry here wrong about a person, so `AnonymizeDialog` and `EditPersonDialog` call
   * `removeQueries` with this root: invalidating would keep the body and fetch it again, which is the
   * opposite of what an erasure asked for.
   */
  duplicateCheck: ['duplicate-check'],
  /**
   * §6.8's family, and it is written by §6.9 too: `active_question_count` is a column of `RoleOut`, so
   * a question created, removed or restored inside a set moves the grid's cell for every vacancy that
   * set is attached to. `roles` is the family's first segment, so it matches every page and every
   * filter of it — and it would reach a single-vacancy key too, on the day one exists again
   * (`queryKeys.role` was that key and is deleted; see the note beside `roleOptions`).
   *
   * ⚠️ **A *create* moves it again, and it did not between 2026-08-11 and 2026-09-24** (ADR 0019).
   * The intervening rule was correct for the model it was written for: a new **library** entry belonged
   * to no set, so no vacancy's count could have moved. A question is now written **into a set** and is
   * active from the instant it exists, so the count of every vacancy offering that set is wrong until
   * it refetches. The note this replaces is kept as history in the paragraph above rather than deleted,
   * because the reasoning was sound and it is the premise that moved.
   *
   * ⚠️ **`queryKeyRoots.questions` stood beside this one and is deleted with the library route** (ADR
   * 0019, 2026-09-24). Its family had one member, `queryKeys.questions`, and both are gone: there is no
   * request left that reads a question outside its set, so every question write invalidates
   * `questionSets` and `roles` and nothing else.
   */
  roles: ['roles'],
  /**
   * ⚠️ **The second family of this surface, and it was the third until 2026-09-24** (ADR 0019 — the
   * library's root is gone). Every write §6.9 offers lands here, and which of them also moves `roles`
   * is decided by one question: does the vacancy's `active_question_count` change?
   *
   *  * **Writing, removing or restoring a question** changes the set's own count — the list row **and**
   *    the detail entry, which is why the detail key lives inside this root — and it moves
   *    `RoleOut.active_question_count` for every vacancy the set is attached to. Both roots.
   *  * **Retiring or restoring a set** moves `roles` too: the catalogue's figure counts the distinct
   *    active questions of a vacancy's **active** sets (`role_queryset`), so retiring a set an attached
   *    vacancy offers takes its questions out of that cell.
   *  * **Creating a set, renaming one, and reordering** move this root alone — a new set is attached to
   *    no vacancy, a name is not a count, and an order is not a count either.
   *
   * Neither root is a prefix of the other, which is what keeps a set write from refetching the
   * catalogue's every page for nothing.
   */
  questionSets: ['question-sets'],
  /**
   * ⚠️ **The family §6.10's four writes move** (TASK-101). A created colleague, a changed group, a
   * retired account and a reissued invitation all change *rows of the directory* — including
   * `invitation_pending`, which is a column of every row — so the whole family is invalidated by root
   * rather than by naming the query the screen happens to hold. §6.5's interviewer picker reads the same
   * family, and it must not keep offering somebody who was retired a moment ago.
   */
  staff: ['staff'],
} as const;

/**
 * Mutation keys, for the one distinction the 401 rule has to make.
 *
 * A 401 from any request means the session ended and the app signs out (`queryClient.ts`) — with
 * exactly one exception: **a 401 from the login request is a wrong password**, an answer about the
 * credentials just typed and not about a session that never existed. Signing "out" of nothing would
 * throw away the sign-in screen's own error before the user could read it, so the login mutation
 * names itself here and the rule steps over it.
 */
export const mutationKeys = {
  signIn: () => ['sign-in'] as const,
} as const;
